/**
 * Console Capture Utility
 * Captures all console output and provides export functionality
 */

class ConsoleCapture {
  constructor() {
    this.logs = []
    this.isCapturing = false
    this.originalMethods = {}
    this.maxLogs = 1000 // Prevent memory issues
    this.initialized = false
    this.statusChangeListeners = []
  }

  /**
   * Add a listener for status changes
   */
  addStatusChangeListener(callback) {
    this.statusChangeListeners.push(callback)
  }

  /**
   * Remove a status change listener
   */
  removeStatusChangeListener(callback) {
    this.statusChangeListeners = this.statusChangeListeners.filter(
      (listener) => listener !== callback
    )
  }

  /**
   * Notify all listeners of status change
   */
  notifyStatusChange() {
    this.statusChangeListeners.forEach((callback) => {
      try {
        callback(this.isCapturing)
      } catch (error) {
        console.error('Error in console capture status change listener:', error)
      }
    })
  }

  /**
   * Start capturing console output
   */
  start() {
    if (this.isCapturing) return

    // Store original console methods
    const methods = ['log', 'info', 'warn', 'error', 'debug']

    methods.forEach((method) => {
      // eslint-disable-next-line no-console
      this.originalMethods[method] = console[method]

      // Override console methods
      // eslint-disable-next-line no-console
      console[method] = (...args) => {
        // Call original method
        this.originalMethods[method](...args)

        // Capture the log
        this.capture(method, args)
      }
    })

    this.isCapturing = true
    this.initialized = true
    this.capture('system', ['Console capture started'])
    this.notifyStatusChange()
  }

  /**
   * Stop capturing console output
   */
  stop() {
    if (!this.isCapturing) return

    // Restore original console methods
    Object.keys(this.originalMethods).forEach((method) => {
      // eslint-disable-next-line no-console
      console[method] = this.originalMethods[method]
    })

    this.capture('system', ['Console capture stopped'])
    this.isCapturing = false
    this.notifyStatusChange()
  }

  /**
   * Capture a log entry
   */
  capture(level, args) {
    // Convert arguments to strings
    const message = args
      .map((arg) => {
        if (arg === null) return 'null'
        if (arg === undefined) return 'undefined'
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2)
          } catch (e) {
            return '[Circular or Non-Serializable Object]'
          }
        }
        return String(arg)
      })
      .join(' ')

    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      stack: this.getStackTrace(),
    }

    this.logs.push(logEntry)

    // Prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs.shift()
    }
  }

  /**
   * Get a simplified stack trace
   */
  getStackTrace() {
    const stack = new Error().stack
    if (!stack) return ''

    const lines = stack.split('\n')
    // Skip the first 3 lines (Error message and internal capture methods)
    const relevantLines = lines.slice(3, 6)
    return relevantLines.join('\n').trim()
  }

  /**
   * Clear all captured logs
   */
  clear() {
    this.logs = []
    this.capture('system', ['Logs cleared'])
  }

  /**
   * Get all captured logs
   */
  getLogs() {
    return this.logs
  }

  /**
   * Export logs as formatted text (with optional filters)
   */
  exportAsText(includeFilter = '', excludeFilter = '') {
    const filteredLogs =
      includeFilter || excludeFilter ? this.searchLogs(includeFilter, excludeFilter) : this.logs

    const header =
      `Console Logs Export\n` +
      `====================\n` +
      `Exported at: ${new Date().toISOString()}\n` +
      `Include Filter: ${includeFilter || 'None'}\n` +
      `Exclude Filter: ${excludeFilter || 'None'}\n` +
      `Filtered logs: ${filteredLogs.length}\n` +
      `Total logs: ${this.logs.length}\n` +
      `====================\n\n`

    const logText = filteredLogs
      .map((log) => {
        const levelPadded = log.level.toUpperCase().padEnd(7)
        return `[${log.timestamp}] [${levelPadded}] ${log.message}`
      })
      .join('\n')

    return header + logText
  }

  /**
   * Export logs as JSON (with optional filters)
   */
  exportAsJSON(includeFilter = '', excludeFilter = '') {
    const filteredLogs =
      includeFilter || excludeFilter ? this.searchLogs(includeFilter, excludeFilter) : this.logs

    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        includeFilter: includeFilter || null,
        excludeFilter: excludeFilter || null,
        filteredCount: filteredLogs.length,
        totalLogs: this.logs.length,
        logs: filteredLogs,
      },
      null,
      2
    )
  }

  /**
   * Format logs for Claude (optimized for AI reading)
   */
  formatForClaude(includeFilter = '', excludeFilter = '') {
    const filteredLogs =
      includeFilter || excludeFilter ? this.searchLogs(includeFilter, excludeFilter) : this.logs

    const header =
      `## Console Logs for Debugging\n` +
      `**Captured at:** ${new Date().toISOString()}\n` +
      `**Include Filter:** ${includeFilter || 'None'}\n` +
      `**Exclude Filter:** ${excludeFilter || 'None'}\n` +
      `**Total Logs:** ${filteredLogs.length} of ${this.logs.length}\n` +
      `---\n\n`

    // Group logs by level for better readability
    const groupedLogs = {}
    filteredLogs.forEach((log) => {
      if (!groupedLogs[log.level]) {
        groupedLogs[log.level] = []
      }
      groupedLogs[log.level].push(log)
    })

    let formattedContent = header

    // Add summary
    formattedContent += `### Summary by Level:\n`
    Object.entries(groupedLogs).forEach(([level, logs]) => {
      formattedContent += `- **${level.toUpperCase()}**: ${logs.length} entries\n`
    })
    formattedContent += `\n`

    // Add the actual logs with better object formatting
    formattedContent += `### Detailed Logs:\n\n`
    formattedContent += '```\n'
    filteredLogs.forEach((log) => {
      const timestamp = new Date(log.timestamp).toLocaleTimeString()
      const level = log.level.toUpperCase().padEnd(7)

      // Check if the message contains JSON objects and format them nicely
      let message = log.message
      if (message.includes('{') && message.includes('}')) {
        // Try to detect and format JSON objects
        try {
          // Check if the entire message is a JSON object
          const parsed = JSON.parse(message)
          message = JSON.stringify(parsed, null, 2)
        } catch {
          // Message might contain mixed content with objects
          // Keep original formatting since it's already been stringified with indentation
          message = log.message
        }
      }

      // For multi-line messages (like formatted objects), indent subsequent lines
      const lines = message.split('\n')
      if (lines.length > 1) {
        formattedContent += `[${timestamp}] [${level}] ${lines[0]}\n`
        for (let i = 1; i < lines.length; i++) {
          formattedContent += `                        ${lines[i]}\n`
        }
      } else {
        formattedContent += `[${timestamp}] [${level}] ${message}\n`
      }
    })
    formattedContent += '```\n'

    return formattedContent
  }

  /**
   * Copy logs to clipboard (with optional filters)
   */
  async copyToClipboard(includeFilter = '', excludeFilter = '') {
    const content = this.formatForClaude(includeFilter, excludeFilter)

    try {
      await navigator.clipboard.writeText(content)
      this.capture('system', [
        `Logs copied to clipboard (include: ${includeFilter || 'none'}, exclude: ${excludeFilter || 'none'})`,
      ])
      return true
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      this.capture('error', ['Failed to copy logs to clipboard'])
      return false
    }
  }

  /**
   * Download logs as a file (with optional filters)
   */
  downloadLogs(format = 'text', includeFilter = '', excludeFilter = '') {
    let content, filename, mimeType

    const filterSuffix = includeFilter || excludeFilter ? `-filtered` : ''

    if (format === 'json') {
      content = this.exportAsJSON(includeFilter, excludeFilter)
      filename = `console-logs${filterSuffix}-${Date.now()}.json`
      mimeType = 'application/json'
    } else {
      content = this.exportAsText(includeFilter, excludeFilter)
      filename = `console-logs${filterSuffix}-${Date.now()}.txt`
      mimeType = 'text/plain'
    }

    // Create blob and download link
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename

    // Trigger download
    document.body.appendChild(link)
    link.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }, 100)

    this.capture('system', [`Logs exported as ${filename}`])
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level) {
    return this.logs.filter((log) => log.level === level)
  }

  /**
   * Get logs within a time range
   */
  getLogsByTimeRange(startTime, endTime) {
    const start = new Date(startTime).getTime()
    const end = new Date(endTime).getTime()

    return this.logs.filter((log) => {
      const logTime = new Date(log.timestamp).getTime()
      return logTime >= start && logTime <= end
    })
  }

  /**
   * Search logs by message content with include and exclude filters
   * @param {string} includeFilter - Patterns to include (comma or pipe separated)
   * @param {string} excludeFilter - Patterns to exclude (comma or pipe separated)
   * Examples:
   *   Include: "[AUTH], [CHAT STATE DEBUG]" - matches logs containing either pattern
   *   Exclude: "debug, trace" - excludes logs containing these patterns
   */
  searchLogs(includeFilter = '', excludeFilter = '') {
    let filteredLogs = this.logs

    // Apply include filter if provided
    if (includeFilter && includeFilter.trim() !== '') {
      // Split by comma or pipe, then trim each term
      const includeTerms = includeFilter
        .split(/[,|]/)
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0)

      if (includeTerms.length > 0) {
        // Keep logs that match ANY of the include terms (OR logic)
        filteredLogs = filteredLogs.filter((log) => {
          const messageLower = log.message.toLowerCase()
          return includeTerms.some((term) => messageLower.includes(term))
        })
      }
    }

    // Apply exclude filter if provided
    if (excludeFilter && excludeFilter.trim() !== '') {
      // Split by comma or pipe, then trim each term
      const excludeTerms = excludeFilter
        .split(/[,|]/)
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0)

      if (excludeTerms.length > 0) {
        // Remove logs that match ANY of the exclude terms
        filteredLogs = filteredLogs.filter((log) => {
          const messageLower = log.message.toLowerCase()
          return !excludeTerms.some((term) => messageLower.includes(term))
        })
      }
    }

    return filteredLogs
  }

  /**
   * Get summary statistics
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {},
    }

    this.logs.forEach((log) => {
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1
    })

    return stats
  }
}

// Create singleton instance
const consoleCapture = new ConsoleCapture()

// Auto-start capturing if enabled via environment variable
// Set VITE_CONSOLE_CAPTURE_ENABLED=true in your .env file to enable
const isConsoleCaputureEnabled = import.meta.env.VITE_CONSOLE_CAPTURE_ENABLED === 'true'

if (isConsoleCaputureEnabled) {
  consoleCapture.start()
}

// Expose to window for easy access
if (typeof window !== 'undefined') {
  window.consoleCapture = consoleCapture
}

export default consoleCapture
